import React, { createContext, useCallback, useEffect, useMemo, useState } from 'react';
import collectAllGroups from '@/app/features/reports/utils/collectAllGroups';
import { ExpandedItemsState, ReportGroupModes } from '../types';
import { useReportViewContext } from '@/app/features/reports/components/report-viewer-context';

// --- Interface Definitions ---

// Represents a node in the processed report tree structure
interface ReportItem {
  id: string;
  title: string;
  content?: unknown[]; // Array of actual test items/content - use a more specific type if available
  children?: ReportItem[]; // Array of nested header/group items
  count?: number;
}

// Type for the hierarchy map value
interface HierarchyNodeInfo {
    parent: string | null;
    isGroup: boolean; // True if it primarily holds content, false if it holds children
    children: Set<string>;
}

// Context for grouping and expansion state
export interface ReportGroupingContext {
    groupMode: ReportGroupModes;
    setGroupMode: React.Dispatch<React.SetStateAction<ReportGroupModes>>;
    expandedItems: Set<string>;
    setExpandedItems: (value: Set<string> | ((prevSet: Set<string>) => Set<string>)) => void;
    expandedTiles: Set<number>;
    setExpandedTiles: React.Dispatch<React.SetStateAction<Set<number>>>;
    expandAll: () => void;
    collapseAll: () => void;
    areAllHeadersExpanded: boolean;
    areAllGroupsExpanded: boolean;
    areAllTilesExpanded: boolean;
    expandAllTiles: () => void;
    collapseAllTiles: () => void;
    totalVisibleTests: number;
}

export const ReportGroupingContext = createContext<ReportGroupingContext>(null!);

export const useReportGroupingContext = () => {
    const context = React.useContext(ReportGroupingContext);
    if (!context) {
        throw new Error('useReportGroupingContext must be used within a ReportGroupingProvider');
    }
    return context;
}

interface ReportGroupingProviderProps {
    children: React.ReactNode;
}

export const ReportGroupingProvider: React.FC<ReportGroupingProviderProps> = ({ children }) => {

    const { data: groupedData, filteredData } = useReportViewContext();
    console.log(`[ReportGroupingProvider] Rendering. Received filteredData length ${filteredData?.length ?? 0} from context.`);

    // Get the groupMode from the reportViewContext
    const { groupMode, setGroupMode } = useReportViewContext();

    // --- Hierarchy Map Generation ---
    const hierarchyMap = useMemo(() => {
        console.log('[grouping-context] Generating hierarchy map for mode:', groupMode);
        const map = new Map<string, HierarchyNodeInfo>();

        function buildHierarchy(items: ReportItem[], parentId: string | null) {
            if (!items || items.length === 0) return;

            items.forEach(item => {
                if (!item || typeof item.id === 'undefined') { // Added safety check
                    console.warn('[grouping-context] Skipping item without id during hierarchy build:', item);
                    return;
                }

                // Determine if it's a group (holds content) or a header (holds children)
                // Nodes with children are considered headers, otherwise groups.
                const hasChildren = item.children && item.children.length > 0;
                const isGroup = !hasChildren; // Simplified: If it has children, it's a header.

                // Get IDs of direct children
                const childIdSet = new Set<string>();
                if (hasChildren) {
                    item.children?.forEach(child => {
                         if (child && typeof child.id !== 'undefined') { // Added safety check
                            childIdSet.add(child.id.toString());
                         }
                    });
                }

                // Add entry to the map
                map.set(item.id.toString(), { // Ensure ID is string
                    parent: parentId,
                    isGroup: isGroup,
                    children: childIdSet
                });

                // Recurse if there are children
                if (hasChildren) {
                    buildHierarchy(item.children!, item.id.toString()); // Pass current item's ID as parent
                }
            });
        }

        // Start building from the root level using groupedData
        if (groupedData && Array.isArray(groupedData)) {
             buildHierarchy(groupedData as ReportItem[], null); // Cast groupedData if necessary
        } else {
            console.warn('[grouping-context] groupedData is not available or not an array for hierarchy map generation.');
        }

        console.log('[grouping-context] Generated hierarchy map:', map);
        return map;
    }, [groupedData, groupMode]); // Now depends on the correctly named groupedData

    const [expandedItems, setExpandedItems] = useState<ExpandedItemsState>({
        element: new Set(),
        location: new Set(),
        test: new Set(),
        rating: new Set(),
        category: new Set()
    });
    const [expandedTiles, setExpandedTiles] = useState<Set<number>>(new Set());
    
    //const [groupMode, setGroupMode] = useState<ReportGroupModes>('location');

    const totalVisibleTests = useMemo(() => filteredData?.length || 0, [filteredData]);

    const currentExpandedItems = useMemo(() => expandedItems[groupMode], [expandedItems, groupMode]);

    const allHeaderIds = useMemo(() => {
        if (!groupedData) return new Set<string>();
        const headers = collectAllGroups(groupedData, new Set<string>(), false);
        console.log('[grouping-context] Memo: allHeaderIds =', headers);
        return headers;
    }, [groupedData]);

    const allGroupIds = useMemo(() => {
        if (!groupedData) return new Set<string>();
        const groups = collectAllGroups(groupedData, new Set<string>(), true);
        console.log('[grouping-context] Memo: allGroupIds =', groups);
        return groups;
    }, [groupedData]);

    const areAllHeadersExpanded = useMemo(() => {
        if (allHeaderIds.size === 0) return true;
        return [...allHeaderIds].every(id => currentExpandedItems.has(id));
    }, [currentExpandedItems, allHeaderIds]);

    const areAllGroupsExpanded = useMemo(() => {
        if (allGroupIds.size === 0) return true;
        return currentExpandedItems.size === allGroupIds.size && [...allGroupIds].every(id => currentExpandedItems.has(id));
    }, [currentExpandedItems, allGroupIds]);

    const areAllTilesExpanded = useMemo(() =>
        totalVisibleTests > 0 && expandedTiles.size === totalVisibleTests,
        [expandedTiles, totalVisibleTests]
    );

    const setGroupExpandedItems = useCallback((value: Set<string> | ((prevSet: Set<string>) => Set<string>)) => {
        setExpandedItems(prev => ({
            ...prev,
            [groupMode]: typeof value === 'function' ? value(prev[groupMode]) : value,
        }));
    }, [groupMode]);

    const collapseAll = useCallback(() => {
        console.log('[grouping-context] collapseAll called. State:', {
            expandedTilesSize: expandedTiles.size,
            currentExpandedItemsSize: currentExpandedItems.size,
            allHeaderIdsSize: allHeaderIds.size // Assuming allHeaderIds is accessible here
        });

        // 1. If any tiles are expanded, collapse all tiles.
        if (expandedTiles.size > 0) {
            console.log('[grouping-context] collapseAll: Stage 1 - Collapsing Tiles');
            setExpandedTiles(new Set());
            return; // Collapse stops here for this click
        }

        // Determine if any groups are expanded using the hierarchy map
        let hasExpandedGroups = false;
        const groupsToCollapse = new Set<string>();
        const headersToKeep = new Set<string>(); // Headers that should remain after collapsing groups

        for (const itemId of currentExpandedItems) {
            const nodeInfo = hierarchyMap.get(itemId);
            if (nodeInfo?.isGroup) {
                hasExpandedGroups = true;
                groupsToCollapse.add(itemId);
                // Ensure the parent header is marked to be kept
                if (nodeInfo.parent && hierarchyMap.has(nodeInfo.parent)) {
                    headersToKeep.add(nodeInfo.parent);
                }
            } else if (hierarchyMap.has(itemId)) {
                // It's a header, mark it to be kept initially
                headersToKeep.add(itemId);
            }
        }

        // 2. If no tiles are expanded, but groups are expanded, collapse groups to headers.
        if (hasExpandedGroups) {
            console.log('[grouping-context] collapseAll: Stage 2 - Collapsing Groups to Headers');
            // Keep headers that were parents of collapsed groups or were already expanded
            setGroupExpandedItems(headersToKeep);
            // Ensure tiles remain collapsed
            setExpandedTiles(new Set());
            return; // Collapse stops here for this click
        }

        // 3. If no tiles or groups are expanded, but headers are, collapse deepest headers incrementally.
        if (currentExpandedItems.size > 0) {
            console.log('[grouping-context] collapseAll: Stage 3 - Collapsing Deepest Headers');
            const nextExpandedItems = new Set<string>();
            let isAnythingCollapsed = false; // Flag to detect if any change occurs

            for (const itemId of currentExpandedItems) {
                const nodeInfo = hierarchyMap.get(itemId);
                console.log('[DEBUG] Checking item:', itemId, 'NodeInfo:', nodeInfo);

                // Check if this item has any children (from hierarchy) that are ALSO currently expanded
                let hasExpandedChildren = false;
                if (nodeInfo?.children) {
                    for (const childId of nodeInfo.children) {
                        if (currentExpandedItems.has(childId)) {
                            hasExpandedChildren = true;
                            break;
                        }
                    }
                }

                console.log('[DEBUG] Has expanded children:', hasExpandedChildren);

                if (hasExpandedChildren) {
                    // This item has expanded children within the current set, so keep it.
                    nextExpandedItems.add(itemId);
                } else {
                    // This item is a 'leaf' in the currently expanded set.
                    // Collapse it by adding its parent (if it exists) to the next state.
                    console.log('[DEBUG] Is leaf, attempting to add parent:', nodeInfo?.parent);
                    isAnythingCollapsed = true; // Mark that we are modifying the set
                    if (nodeInfo?.parent && hierarchyMap.has(nodeInfo.parent)) {
                        nextExpandedItems.add(nodeInfo.parent);
                    }
                }
            }

            // Check if the state actually changed. If not (e.g., only one top-level item left, or no leaves found),
            // or if nothing was flagged for collapse, force collapse all remaining items.
            const setsAreEqual = currentExpandedItems.size === nextExpandedItems.size &&
                                 [...currentExpandedItems].every(id => nextExpandedItems.has(id));

            console.log('[DEBUG] After loop: isAnythingCollapsed:', isAnythingCollapsed, 'setsAreEqual:', setsAreEqual);
            console.log('[DEBUG] Current Expanded:', currentExpandedItems);
            console.log('[DEBUG] Next Expanded:', nextExpandedItems);

            if (!isAnythingCollapsed || setsAreEqual) {
                 console.log('[grouping-context] collapseAll: Stage 3b - Collapsing remaining headers / preventing loop');
                 setGroupExpandedItems(new Set<string>());
            } else {
                setGroupExpandedItems(nextExpandedItems);
            }

            // Ensure tiles remain collapsed
            setExpandedTiles(new Set());
            return; // Collapse stops here for this click
        }

        // 4. If already fully collapsed
        console.log('[grouping-context] collapseAll: No action needed (already fully collapsed)');

    }, [
        expandedTiles,
        currentExpandedItems,
        hierarchyMap, // Added dependency
        setGroupExpandedItems,
        setExpandedTiles
    ]);

    const expandAll = useCallback(() => {
        if (!groupedData) {
            console.log('[grouping-context] expandAll called but no data, returning');
            return;
        }
        console.log('[grouping-context] expandAll called. Current state:', { areAllHeadersExpanded, areAllGroupsExpanded, areAllTilesExpanded, totalVisibleTests });

        if (!areAllHeadersExpanded) {
            console.log('[grouping-context] expandAll: Stage 1 - Expanding Headers');
            setGroupExpandedItems(allHeaderIds);
            setExpandedTiles(new Set());
        } else if (!areAllGroupsExpanded) {
            console.log('[grouping-context] expandAll: Stage 2 - Expanding Groups');
            setGroupExpandedItems(allGroupIds);
            setExpandedTiles(new Set());
        } else if (!areAllTilesExpanded && totalVisibleTests > 0) {
            console.log('[grouping-context] expandAll: Stage 3 - Expanding Tiles');
            setExpandedTiles(new Set(filteredData!.map((item) => item.assessmentTestId)));
        } else {
            console.log('[grouping-context] expandAll: No action taken (already expanded or no tiles)');
        }
    }, [
        areAllHeadersExpanded,
        areAllGroupsExpanded,
        areAllTilesExpanded,
        groupedData,
        filteredData,
        allHeaderIds,
        allGroupIds,
        totalVisibleTests,
        setGroupExpandedItems,
        setExpandedTiles
    ]);

    const expandAllTiles = useCallback(() => {
        console.log('[grouping-context] expandAllTiles called. State:', { areAllGroupsExpanded, totalVisibleTests });
        if (!filteredData || totalVisibleTests === 0) {
            console.log('[grouping-context] expandAllTiles: No filtered data or tests, returning.');
            return;
        }

        if (!areAllGroupsExpanded) {
            console.log('[grouping-context] expandAllTiles: Expanding groups first.');
            setGroupExpandedItems(allGroupIds);
        }
        console.log('[grouping-context] expandAllTiles: Expanding tiles.');
        setExpandedTiles(new Set(filteredData.map((item) => item.assessmentTestId)));
    }, [
        filteredData,
        totalVisibleTests,
        areAllGroupsExpanded,
        allGroupIds,
        setGroupExpandedItems,
        setExpandedTiles
    ]);

    const collapseAllTiles = useCallback(() => {
        console.log('[grouping-context] collapseAllTiles called.');
        // Mirror expandAllTiles, which force-opens groups as a prerequisite for showing tile
        // content — collapsing tiles without also collapsing those groups back down left them
        // stuck open forever in any view with no separate tile-vs-group visual (e.g. the
        // compact SDA table), making "Collapse All Tiles" look completely inert
        // (raytd/web QA 108.03 item 16, QA 108.04 item 2).
        setExpandedTiles(new Set());
        setGroupExpandedItems(new Set());
    }, [setExpandedTiles, setGroupExpandedItems]);

    useEffect(() => {
        console.log('[grouping-context] State Update (useEffect):', {
            groupMode,
            currentExpandedItemsSize: currentExpandedItems.size,
            allHeaderIdsSize: allHeaderIds.size,
            allGroupIdsSize: allGroupIds.size,
            areAllHeadersExpanded,
            areAllGroupsExpanded,
            expandedTilesSize: expandedTiles.size,
            totalVisibleTests,
            areAllTilesExpanded,
        });
    }, [
        groupMode,
        currentExpandedItems,
        allHeaderIds,
        allGroupIds,
        areAllHeadersExpanded,
        areAllGroupsExpanded,
        expandedTiles,
        totalVisibleTests,
        areAllTilesExpanded,
    ]);

    const value = useMemo(() => ({
        groupMode,
        setGroupMode,
        expandedItems: currentExpandedItems,
        setExpandedItems: setGroupExpandedItems,
        expandedTiles,
        setExpandedTiles,
        expandAll,
        collapseAll,
        areAllHeadersExpanded,
        areAllGroupsExpanded,
        areAllTilesExpanded,
        expandAllTiles,
        collapseAllTiles,
        totalVisibleTests,
    }), [
        groupMode, setGroupMode, currentExpandedItems, setGroupExpandedItems,
        expandedTiles, setExpandedTiles, expandAll, collapseAll,
        areAllHeadersExpanded, areAllGroupsExpanded, areAllTilesExpanded,
        expandAllTiles, collapseAllTiles, totalVisibleTests
    ]);

    return (
        <ReportGroupingContext.Provider value={value}>
            {children}
        </ReportGroupingContext.Provider>
    );
};
